import type { ReactNode } from 'react';
import { Icon, type IconName } from '../Icon';

type Variant = 'default' | 'primary' | 'brand' | 'amber' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps {
  children?: ReactNode;
  variant?: Variant;
  size?: Size;
  onClick?: () => void;
  disabled?: boolean;
  icon?: IconName;
  iconRight?: IconName;
  full?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  className?: string;
}

const variants: Record<Variant, { bg: string; fg: string; bd: string }> = {
  primary: { bg: 'var(--accent)', fg: 'var(--accent-fg)', bd: 'transparent' },
  brand: { bg: 'var(--brand)', fg: '#fff', bd: 'transparent' },
  amber: { bg: 'var(--amber)', fg: 'oklch(0.28 0.04 70)', bd: 'transparent' },
  default: { bg: 'var(--bg-1)', fg: 'var(--text-0)', bd: 'var(--border)' },
  ghost: { bg: 'transparent', fg: 'var(--text-1)', bd: 'transparent' },
  outline: { bg: 'transparent', fg: 'var(--text-0)', bd: 'var(--border)' },
  danger: {
    bg: 'var(--danger-bg)',
    fg: 'var(--danger)',
    bd: 'color-mix(in oklch, var(--danger) 35%, transparent)'
  }
};

export function Button({
  children,
  variant = 'default',
  size = 'md',
  onClick,
  disabled,
  icon,
  iconRight,
  full,
  type = 'button',
  title,
  className = ''
}: ButtonProps) {
  const pad = size === 'sm' ? '5px 10px' : size === 'lg' ? '10px 18px' : '7px 13px';
  const fs = size === 'sm' ? 12.5 : 14;
  const v = variants[variant];
  const iconSize = size === 'sm' ? 14 : 15;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`focusable ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        padding: pad,
        fontSize: fs,
        fontWeight: 500,
        borderRadius: 'var(--r-sm)',
        width: full ? '100%' : 'auto',
        letterSpacing: '-0.01em',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'background 0.14s, border-color 0.14s, transform 0.06s, opacity 0.14s',
        whiteSpace: 'nowrap',
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.bd}`
      }}
    >
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </button>
  );
}
